import React, { useState, useEffect } from "react";
import ShifaraLogo from "src/assets/images/shifara-green-logo.png";
import RenderText from "../Text/RenderText";
import SimpleInput from "../SimpleInput";
import GradientBtn from "../WebsiteComponents/GradientBtn";
import GoogleIcon from "src/assets/images/google-icon.png";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { set } from "react-hook-form";
import DrawerTitle from "../Drawer/DrawerTitle";
import VerificationInput from "react-verification-input";
import { toast } from "react-toastify";
import { NewInputUiStyles } from "src/lib/constans";
import httpRequest from "src/helpers/httpRequest";
import { Loader, Loader2 } from "lucide-react";
import { CircularProgress } from "@mui/material";
import { removeAuth, setAuth, setUserDetails } from "src/Store/reducer";
import doctor, { setDoctorState } from "src/Store/doctor";
import { removeAllIndicators } from "src/Store/redDots";
import {
  getNameFromEmail,
  handleGetSelectedAddress,
  removeToken,
} from "src/helpers/helperFunctions";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";
import moment from "moment";
import { clearCart, setCurrentAddress } from "src/Store/cartSlice";
import { setCartItems } from "src/Store/Cart";

interface ILoginCustomerProps {
  onBack?: () => void;
  backBtn?: boolean;
  onSuccess?: () => void;
}

interface IOTPResponse {
  otpExpire?: string;
  message?: string;
  [key: string]: any; // Allow for any additional properties
}

interface IGOOGLE_PROFILE {
  id: string;
  email: string;
  verified_email: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
}

const LoginCustomer: React.FC<ILoginCustomerProps> = ({
  onBack,
  backBtn,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [email, setEmail] = useState<string>("");
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [emailError, setEmailError] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState<number>(120);
  const [otpScreen, setOtpScreen] = useState<boolean>(false);
  const [bool, setBool] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [otpResendLoading, setOtpResendLoading] = useState<boolean>(false);
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [googleLoginLoading, setGoogleLoginLoading] = useState<boolean>(false);
  const [isAccountDeactivated, setIsAccountDeactivated] = useState<string | null>(null);
  const { pharmacyProducts, diagnosticProducts } = useSelector(
    (state: RootState) => state.cartSlice
  );

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      console.log("Google login successful:", tokenResponse);
      setUser(tokenResponse);
    },
    onError: (error) => {
      console.error("Google login failed:", error);
    },
  });

  // Fetch user details when tokenResponse is available
  useEffect(() => {
    if (user?.access_token) {
      axios
        .get("https://www.googleapis.com/oauth2/v1/userinfo", {
          headers: {
            Authorization: `Bearer ${user.access_token}`,
            Accept: "application/json",
          },
        })
        .then((res: { data: IGOOGLE_PROFILE }) => {
          setProfile(res.data);
          handleLogin(res?.data?.email, true, {
            firstName: res?.data?.given_name,
            lastName: res?.data?.family_name,
            profileImageUrl: res?.data?.picture,
          });
        })
        .catch((err) => {
          console.error("Error fetching user info:", err);
        });
    }
  }, [user]);

  const fetchProfile = async (_token: string) => {
    const { res, err } = await httpRequest<any>({
      path: "/customer/profile",
      token: _token,
    });
    if (res) {
      dispatch(setUserDetails(res?.payload));
      const cartResponse: any = await httpRequest({
        path: "/cart/add",
        method: "post",
        token: _token,
        params: {
          items: [
            ...pharmacyProducts.map((item) => ({
              productId: item?._id,
              type: "pharmacy",
              quantity: item?.quantity,
            })),
            ...diagnosticProducts.map((item) => ({
              productId: item?._id,
              type: item?.itemType,
              quantity: item?.quantity,
            })),
          ],
        },
      });
      if (cartResponse.err) {
        console.log(cartResponse.err, "err");
        toast.error(cartResponse.err.message || "Error adding to cart");
        return;
      } else {
        dispatch(clearCart("pharmacyProducts"));
        dispatch(clearCart("diagnosticProducts"));
        console.log(cartResponse.res, "res");
        const items = cartResponse.res?.cartItems || [];
        dispatch(setCartItems([...items]));
      }

      if (res?.payload?.userRole === doctor) {
        dispatch(setDoctorState(res?.payload?._id));
      }
      let selectedAddress = await handleGetSelectedAddress();
      if (selectedAddress) {
        dispatch(setCurrentAddress(selectedAddress));
      }
    } else {
      toast(err, { type: "error" });
      dispatch(removeAuth());
      dispatch(removeAllIndicators());
      removeToken();
    }
  };

  const handleLogin = async (
    e: string = email,
    isGoogle = false,
    initialParams: any = null
  ) => {
    if (!e) {
      setEmailError("Email is required");
      return;
    }
    let emailPattern = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(e)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (isGoogle) {
      setGoogleLoginLoading(true);
    } else {
      setLoginLoading(true);
    }

    if (!initialParams) {
      initialParams = isGoogle
        ? getNameFromEmail(e)
        : {
            firstName: "",
            lastName: "",
            profileImageUrl: "",
          };
      console.log("initialParams", initialParams);
    }

    const { err, res, status } = await httpRequest<any>({
      path: `/customer/new-login`,
      params: {
        email: e?.toLowerCase(),
        isGoogle,
        ...initialParams,
      },
      method: "post",
    });
    if (err) {
      setGoogleLoginLoading(false);
      setLoginLoading(false);
      if(status === 403 && err === "Your account has been deactivated by the admin."){
        setIsAccountDeactivated("Your account is deactivated");
      }
      return toast.error(err || `Login Failed`);
    }
    if (!isGoogle) {
      setOtpScreen(true);
      setEmailError("");
      setTimeLeft(120);
    } else {
      toast.success("You are logged in successfully");
      dispatch(setAuth(res?.token));
      sessionStorage.removeItem("customerTwoFactor");
      fetchProfile(res?.token);
      onSuccess?.();
    }
    setGoogleLoginLoading(false);
    setLoginLoading(false);
  };

  useEffect(() => {
    if (otpScreen && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpScreen, timeLeft]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes < 10 ? "0" : ""}${minutes}:${
      secs < 10 ? "0" : ""
    }${secs}`;
  };

  const handleResend = async () => {
    if (timeLeft > 0) {
      return;
    }
    setOtpResendLoading(true);
    const { res, err } = await httpRequest<IOTPResponse>({
      method: "post",
      path: "/customer/recentOtp",
      params: { email: email.toLowerCase(), role: "customer" },
    });
    if (res) {
      let { message = "" } = res;
      toast(message, { type: "success" });
      setOtp("");
      setTimeLeft(120);
      // Store relevant information in cookie
      let obj = {
        email,
        otpExpire: moment().add(2, "minutes").toISOString(),
        purpose: "login",
      };
      sessionStorage.setItem(
        "customerTwoFactor" as string,
        JSON.stringify(obj)
      );
      setOtpResendLoading(false);
    } else {
      toast(err, { type: "error" });
      setOtpResendLoading(false);
    }
  };

  const handleVerifyOtp = async (otps_number: string = otp) => {
    if (!otps_number) {
      toast("OTP is required!", { type: "error" });
      return;
    }
    if (otps_number.length < 4) {
      toast("OTP must be 4 digits!", { type: "error" });
      return;
    }
    setOtpLoading(true);
    const { res, err } = await httpRequest<{ message: string; token: string }>({
      method: "post",
      path: "/customer/verify-otp",
      params: { otp: otps_number, email: email.toLowerCase() },
    });
    if (res) {
      dispatch(setAuth(res?.token));
      toast("Verification Sucessfull! You are log in to website.", {
        type: "success",
      });
      sessionStorage.removeItem("customerTwoFactor");
      fetchProfile(res?.token);
      onSuccess?.();
    } else {
      toast(err, { type: "error" });
    }
    setOtpLoading(false);
  };

  return (
    <>
      <DrawerTitle
        title="Login with Email"
        backBtn={backBtn || otpScreen || !!onBack}
        onBack={() => {
          if (!otpScreen && onBack) {
            onBack();
            setEmail("");
            setTimeLeft(120);
            setBool(false);
          }
          setOtpScreen(false);
        }}
      />
      <div className="w-full side-bar-padding-2 side-bar-gap flex flex-col items-center justify-center">
        <img src={ShifaraLogo} alt="Shifara Logo" className="w-[40%]" />
        <RenderText
          fontFamily="Poppins-Regular"
          className="text-center font-size-16px max-w-[70%] 2xl:mt-5 mt-3"
        >
          Your Complete Health Partner – From Medicines to Lab Tests to Home
          Care, All in One Trusted Place
        </RenderText>
        <div className="w-full 2xl:rounded-lg rounded-md  border border-gray-300 2xl:mt-5 mt-4 flex flex-col p-2.5 2xl:p-3">
          {otpScreen ? (
            <>
              <p className="text-center font-size-20px font-Poppins-Medium theme-color-green">
                Verify OTP
              </p>
              <p className="text-center font-size-16px font-Poppins-Regular mt-3">
                {email}{" "}
                <button
                  onClick={() => setOtpScreen(false)}
                  className="underline font-Poppins-Medium"
                >
                  Not You?
                </button>
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleVerifyOtp(otp);
                }}
                className="w-full"
              >
                <div className="verification-container w-full flex justify-center items-center">
                  <VerificationInput
                    autoFocus={true}
                    length={4}
                    validChars="0-9"
                    placeholder="-"
                    value={otp}
                    onChange={(e) => setOtp(e)}
                    // onComplete={(e) => handleVerifyOtp(e)}
                  />
                </div>
                <p className="text-center font-Poppins-Regular font-size-16px mt-3">
                  {formatTime(timeLeft)}
                </p>
                <p className="text-center font-Poppins-Regular flex items-center justify-center xl:gap-2 gap-1 font-size-16px mt-3">
                  Didn't receive the code?{" "}
                  <button
                    onClick={handleResend}
                    className={`font-Poppins-Medium flex items-center gap-2 ${
                      // otpResendLoading
                      otpResendLoading || timeLeft > 0
                        ? "opacity-50 cursor-not-allowed"
                        : "underline"
                    }`}
                    type="button"
                  >
                    {otpResendLoading ? (
                      <>
                        <CircularProgress size={20} color="primary" />{" "}
                        Resending...
                      </>
                    ) : (
                      "Request Again?"
                    )}
                  </button>
                </p>
                <GradientBtn
                  ButtonText="Verify and Login"
                  id="verify-otp-btn"
                  type="submit"
                  // onPress={() => handleVerifyOtp(otp)}
                  btnExtraStyle={{
                    width: "100%",
                    marginTop: "10px",
                  }}
                  loader={otpLoading}
                  disabled={otpLoading || otpResendLoading}
                />
              </form>
            </>
          ) : (
            <>
              <div className="w-full flex flex-col gap-0.5">
                <SimpleInput
                  value={email}
                  onChange={(value) => {
                    setEmail(value);
                    setEmailError("");
                  }}
                  isLabel
                  labelText="Email"
                  error={emailError}
                  newUi
                  extraStyle={{ ...NewInputUiStyles }}
                  disabled={googleLoginLoading || loginLoading}
                  onEnter={() => handleLogin(email, false)}
                  id="login-email-input-new"
                />
              </div>
              <GradientBtn
                ButtonText="Continue"
                onPress={() => handleLogin(email, false)}
                btnExtraStyle={{
                  width: "100%",
                }}
                loader={loginLoading}
                disabled={googleLoginLoading}
                id="login-continue-btn"
              />
              <div className="flex items-center justify-center 2xl:mt-3 mt-2 relative">
                <span className="block border-b border-gray-300 w-full absolute top-1/2 -translate-y-1/2 z-[-1]"></span>
                <RenderText className="uppercase bg-[#F6FAFF] px-2 font-Poppins-Medium font-size-16px text-[#2F2F31]">
                  Or Login with
                </RenderText>
              </div>
              <div className="flex items-center justify-center 2xl:mt-3 mt-2">
                <div
                  id="google-login-btn"
                  className="2xl:w-[55px] 2xl:h-[50px] w-[45px] h-[40px] relative shadow-all rounded flex items-center cursor-pointer justify-center"
                  onClick={() => loginWithGoogle()}
                >
                  {googleLoginLoading && (
                    <div className="w-full absolute top-0 left-0 h-full flex items-center justify-center bg-white/60">
                      <CircularProgress size={20} color="primary" />
                    </div>
                  )}
                  <img
                    src={GoogleIcon}
                    alt="Google Login"
                    className="w-[70%]"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default LoginCustomer;
